import React, { useState } from "react";
import { Building2, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * CMS-driven facilities grid for institute public sites.
 * Reads `items` from the public website endpoint (Active only). When a facility
 * has multiple photos, the card hosts a mini-slider with arrow controls.
 *
 * Themes mirror the institute aesthetics; all colors are passed as Tailwind
 * class strings so the component stays stateless and SSR-friendly.
 *
 * Props:
 *   items          — Facility[] from API: [{id, name, description, photos:[{url}]}]
 *   eyebrow        — small eyebrow above heading
 *   heading        — page-level heading
 *   accent         — Tailwind classes used on the heading underline + chips
 *   testidPrefix   — testid prefix
 */
export default function FacilitiesGrid({
  items = [],
  testidPrefix = "facilities",
  cardClassName = "bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all",
  bodyTextClass = "text-sm text-slate-600 leading-relaxed",
  accentChipClass = "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/20",
}) {
  if (!items?.length) {
    return (
      <div
        data-testid={`${testidPrefix}-empty`}
        className="bg-white/70 border border-dashed border-slate-300 rounded-lg py-14 text-center"
      >
        <Building2 size={36} strokeWidth={1.2} className="mx-auto text-slate-400 mb-3" />
        <div className="text-slate-900 font-medium">Facility information coming soon.</div>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`${testidPrefix}-grid`}>
      {items.map((f) => (
        <FacilityCard
          key={f.id || f.name}
          f={f}
          testidPrefix={testidPrefix}
          cardClassName={cardClassName}
          bodyTextClass={bodyTextClass}
          accentChipClass={accentChipClass}
        />
      ))}
    </div>
  );
}

function FacilityCard({ f, testidPrefix, cardClassName, bodyTextClass, accentChipClass }) {
  const photos = (f.photos || []).filter((p) => p && p.url);
  const [i, setI] = useState(0);
  const total = photos.length;
  const cur = total ? photos[Math.min(i, total - 1)] : null;
  const prev = () => setI((x) => (x - 1 + total) % total);
  const next = () => setI((x) => (x + 1) % total);

  return (
    <div className={cardClassName} data-testid={`${testidPrefix}-card-${f.id || ""}`}>
      <div className="aspect-[16/10] bg-slate-100 relative overflow-hidden">
        {cur ? (
          <img
            src={cur.url}
            alt={`${f.name} — ${i + 1}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <Building2 size={36} strokeWidth={1.2} />
          </div>
        )}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              className="absolute top-1/2 -translate-y-1/2 left-2 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-slate-800 shadow flex items-center justify-center"
              data-testid={`${testidPrefix}-prev`}
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              className="absolute top-1/2 -translate-y-1/2 right-2 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-slate-800 shadow flex items-center justify-center"
              data-testid={`${testidPrefix}-next`}
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
            <div className="absolute bottom-2 right-2 bg-black/65 text-white text-[11px] px-2 py-0.5 rounded-sm font-semibold backdrop-blur-sm">
              {i + 1} / {total}
            </div>
          </>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-slate-900 leading-snug">{f.name}</h3>
        {f.description && <p className={`${bodyTextClass} mt-2`}>{f.description}</p>}
        {total > 0 && (
          <div className={`inline-flex items-center mt-3 text-[11px] tracking-wide uppercase font-semibold px-2 py-0.5 rounded-sm ${accentChipClass}`}>
            {total} photo{total > 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
}
